import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaYoutube, FaBookmark, FaVideo, FaArrowRight, FaPlay, FaCheckCircle, FaCode } from 'react-icons/fa';
import {
  trackSignupFree,
  trackStartFree,
  useYouTubeIframeAnalytics,
  withYouTubeAnalyticsParams
} from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import wordpressLogo from '../assets/images/platforms/wordpress.png';
import wixLogo from '../assets/images/platforms/wix.svg';
import squarespaceLogo from '../assets/images/platforms/squarespace.svg';
import webflowLogo from '../assets/images/platforms/webflow.svg';
import shopifyLogo from '../assets/images/platforms/shopify.svg';
import joomlaLogo from '../assets/images/platforms/joomla.svg';
import './Integration.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

function Integration() {
  const [hoveredPlatform, setHoveredPlatform] = useState(null);
  const [showTutorialPlayer, setShowTutorialPlayer] = useState(false);
  const tutorialIframeRef = useYouTubeIframeAnalytics('WordPress Integration Tutorial', showTutorialPlayer);

  useEffect(() => setPageSEO({
    title: 'Clientalio Integrations - Add Trust Where Buyers Decide',
    description: 'Add customer proof to your website quickly so buyers see trust signals before they leave, compare, or contact you.',
    canonical: 'https://clientalio.com/integration',
    ogTitle: 'Clientalio Integrations - Add Trust Where Buyers Decide',
    ogDescription: 'Publish customer proof on your website in minutes and build credibility before people contact you.',
    twitterTitle: 'Clientalio Integrations - Add Trust Where Buyers Decide',
    twitterDescription: 'Add customer proof to your website quickly so buyers see trust signals before they leave.'
  }), []);

  const platforms = [
    {
      name: "WordPress",
      logo: wordpressLogo,
      description: "Seamlessly integrate with WordPress. Add testimonials with our easy-to-use plugin.",
      features: [
        "One-click plugin installation",
        "Widget support",
        "Shortcode integration",
        "Gutenberg blocks"
      ],
      difficulty: "Easy",
      setupTime: "< 5 mins",
      youtubeId: "dQw4w9WgXcQ", // Replace with actual WordPress tutorial ID
      guideUrl: "#wordpress-guide",
      tutorialUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
    },
    {
      name: "Shopify",
      logo: shopifyLogo,
      description: "Boost conversions with testimonials on your Shopify store. Install in seconds.",
      features: [
        "Shopify app store",
        "Product page widgets",
        "Collection integration",
        "Theme compatibility"
      ],
      difficulty: "Easy",
      setupTime: "< 3 mins",
      guideUrl: "#shopify-guide",
      tutorialUrl: "#shopify-tutorial"
    },
    {
      name: "Wix",
      logo: wixLogo,
      description: "Add testimonials to your Wix website in minutes with our drag-and-drop widget.",
      features: [
        "Drag-and-drop widget",
        "Auto-sync testimonials",
        "Mobile responsive",
        "Custom styling"
      ],
      difficulty: "Easy",
      setupTime: "< 2 mins",
      guideUrl: "#wix-guide",
      tutorialUrl: "#wix-tutorial"
    },
    {
      name: "Squarespace",
      logo: squarespaceLogo,
      description: "Display testimonials on your Squarespace site with our embed code.",
      features: [
        "Simple embed code",
        "Template compatible",
        "Automatic updates",
        "Responsive design"
      ],
      difficulty: "Easy",
      setupTime: "< 5 mins",
      guideUrl: "#squarespace-guide",
      tutorialUrl: "#squarespace-tutorial"
    },
    {
      name: "Webflow",
      logo: webflowLogo,
      description: "Perfect integration with Webflow using custom HTML embed.",
      features: [
        "Custom HTML embed",
        "CSS customization",
        "Dynamic content",
        "CMS integration"
      ],
      difficulty: "Medium",
      setupTime: "10-15 mins",
      guideUrl: "#webflow-guide",
      tutorialUrl: "#webflow-tutorial"
    },
    {
      name: "Joomla",
      logo: joomlaLogo,
      description: "Integrate testimonials into your Joomla website with our extension.",
      features: [
        "Joomla extension",
        "Module positions",
        "Component integration",
        "Multi-language support"
      ],
      difficulty: "Medium",
      setupTime: "10-15 mins",
      guideUrl: "#joomla-guide",
      tutorialUrl: "#joomla-tutorial"
    }
  ];

  const integrationMethods = [
    {
      icon: "🔌",
      title: "Direct Plugin Installation",
      description: "Install our official plugins directly from your CMS marketplace. One-click installation with automatic updates."
    },
    {
      icon: "💻",
      title: "Embed Code",
      description: "Copy and paste our embed code anywhere on your website. Works with any platform that supports HTML/JavaScript."
    },
    {
      icon: "🔗",
      title: "API Integration",
      description: "Use our REST API to build custom integrations. Full documentation and code examples provided."
    },
    {
      icon: "⚙️",
      title: "Webhook Support",
      description: "Receive real-time updates when new testimonials are submitted. Integrate with Zapier and other automation tools."
    }
  ];

  const additionalIntegrations = [
    { name: "HubSpot", icon: "🎯" },
    { name: "Mailchimp", icon: "📧" },
    { name: "Zapier", icon: "⚡" },
    { name: "Slack", icon: "💬" },
    { name: "Google Analytics", icon: "📊" },
    { name: "Facebook Pixel", icon: "📘" }
  ];

  return (
    <div className="integration-page">
      {/* Hero Section */}
      <motion.section
        className="integration-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container integration-hero-layout">
          <motion.div
            className="integration-hero-copy"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <span className="integration-eyebrow">INSTALL CUSTOMER PROOF, NOT COMPLEXITY</span>
            <h1>Add trust where your buyers already decide.</h1>
            <p>Publish customer stories on any website with a plugin, embed, or API. Start with a guided setup and keep every widget in sync automatically.</p>
            <div className="hero-actions">
              <a className="integration-primary-action" href="/signup" onClick={() => trackStartFree('integration_hero')}>Start free <FaArrowRight /></a>
              <Link className="integration-secondary-action" to="/integration-documentation"><FaCode /> View documentation</Link>
            </div>
            <div className="integration-hero-proof">
              <span><FaCheckCircle /> No-code setup</span>
              <span><FaCheckCircle /> Responsive by default</span>
              <span><FaCheckCircle /> Automatic updates</span>
            </div>
          </motion.div>

          <motion.aside
            className="integration-console"
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="integration-console-bar"><span /><span /><span /><strong>clientalio / publish</strong></div>
            <div className="integration-console-body">
              <span className="integration-console-label">EMBED STATUS</span>
              <h2>Your testimonial wall is live.</h2>
              <div className="integration-code-block">
                <code>&lt;script src=&quot;clientalio.com/widget.js&quot;&gt;&lt;/script&gt;</code>
              </div>
              <div className="integration-console-status"><i /><span><strong>Connected</strong> Auto-sync is enabled</span></div>
              <div className="integration-console-metrics"><span><strong>1</strong> embed</span><span><strong>6</strong> platforms</span><span><strong>&lt;2m</strong> setup</span></div>
            </div>
          </motion.aside>
        </div>
      </motion.section>

      <section className="integration-platform-rail" aria-label="Supported website platforms">
        <div className="container integration-platform-rail-inner">
          <p>Works with the tools your website already uses</p>
          <div>
            {platforms.map((platform) => (
              <span key={platform.name}><img src={platform.logo} alt="" />{platform.name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Methods */}
      {/* <motion.section 
        className="integration-methods"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Multiple Integration Methods</h2>
            <p>Choose the integration method that works best for your platform</p>
          </motion.div>
          <motion.div 
            className="methods-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {integrationMethods.map((method, index) => (
              <motion.div 
                key={index} 
                className="method-card"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
              >
                <div className="method-icon">{method.icon}</div>
                <h3>{method.title}</h3>
                <p>{method.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section> */}

      {/* Supported Platforms */}
      {/* <motion.section 
        className="platforms-section"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Supported Integrations</h2>
            <p>Works seamlessly with major platforms and CMS systems</p>
          </motion.div>
          <motion.div 
            className="platforms-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {platforms.map((platform, index) => (
              <motion.div 
                key={index} 
                className="platform-card"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                onHoverStart={() => setHoveredPlatform(index)}
                onHoverEnd={() => setHoveredPlatform(null)}
              >
                <div className="platform-header">
                  <div className="platform-icon">
                    <img src={platform.logo} alt={`${platform.name} logo`} />
                  </div>
                  <div className="platform-info">
                    <h3>{platform.name}</h3>
                    <span className={`difficulty ${platform.difficulty.toLowerCase()}`}>
                      {platform.difficulty}
                    </span>
                  </div>
                </div>
                <p className="platform-description">{platform.description}</p>
                <ul className="platform-features">
                  {platform.features.map((feature, idx) => (
                    <li key={idx}>✓ {feature}</li>
                  ))}
                </ul>
                <div className="setup-time">
                  <span>⏱️ Setup time: {platform.setupTime}</span>
                </div>
                <div className="platform-actions">
                  <a href={platform.guideUrl} className="action-btn guide-btn">
                    <FaBookmark /> View Guide
                  </a>
                  <a href={platform.tutorialUrl} className="action-btn tutorial-btn">
                    <FaVideo /> Tutorial
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section> */}

      {/* Integration Documentation Preview */}
      <motion.section
        className="integration-doc-preview"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>See How Fast You Can Publish Proof</h2>
            <p>Use simple guides to add customer stories to the pages where buyers decide.</p>
          </motion.div>
          <motion.div
            className="doc-preview-browser"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <div className="doc-preview-browser-top">
              <div className="doc-preview-browser-dots" aria-hidden="true">
                <span className="dot dot-red"></span>
                <span className="dot dot-yellow"></span>
                <span className="dot dot-green"></span>
              </div>
              <span className="doc-preview-browser-url">clientalio.com/integration-documentation</span>
              <Link to="/integration-documentation" className="doc-preview-open-link">
                Open full guide
              </Link>
            </div>
            <iframe
              className="doc-preview-iframe"
              src="/integration-documentation?preview=1"
              title="Integration documentation preview"
            ></iframe>
          </motion.div>
        </div>
      </motion.section>

      {/* WordPress Tutorial Embed */}
      <motion.section
        className="tutorial-section"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Publish Trust on WordPress Fast</h2>
            <p>Watch how quickly customer proof can appear on your website.</p>
          </motion.div>
          <motion.div
            className="video-embed-wrapper"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="video-embed custom-video-player">
              {!showTutorialPlayer ? (
                <div
                  className="tutorial-video-poster"
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowTutorialPlayer(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setShowTutorialPlayer(true);
                    }
                  }}
                  aria-label="Play WordPress integration tutorial"
                >
                  <img
                    src="https://img.youtube.com/vi/W3OsKNwVD74/maxresdefault.jpg"
                    alt="WordPress Integration Tutorial"
                  />
                  <div className="tutorial-video-overlay">
                    <button className="tutorial-play-button" type="button" aria-hidden="true">
                      <FaPlay />
                    </button>
                    <div className="tutorial-video-meta">
                      <span><FaYoutube /> Clientalio Tutorial</span>
                      <strong>WordPress Integration Tutorial</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  ref={tutorialIframeRef}
                  src={withYouTubeAnalyticsParams('https://www.youtube.com/embed/W3OsKNwVD74?autoplay=1&rel=0&modestbranding=1')}
                  title="WordPress Integration Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Additional Integrations */}
      {/* <motion.section 
        className="additional-integrations"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Connected Tools</h2>
            <p>Integrates with popular marketing and analytics platforms</p>
          </motion.div>
          <motion.div 
            className="additional-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {additionalIntegrations.map((integration, index) => (
              <motion.div 
                key={index} 
                className="additional-card"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
              >
                <div className="additional-icon">{integration.icon}</div>
                <p>{integration.name}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section> */}

      {/* Need Another Integration */}
      {/* <motion.section
        className="need-integration"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2>Need Another Integration?</h2>
            <p>Don't see your platform? Contact us and we'll help add it.</p>
            
          </motion.div>
        </div>
      </motion.section> */}

      {/* Final CTA */}
      <motion.section
        className="integration-cta"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2>Ready to Put Customer Proof Where Buyers Decide?</h2>
            <p>Create your branded page, share your first request, and publish trust signals without waiting on a developer.</p>
            <div className="cta-buttons">
<button
  className="btn-primary"
  onClick={() => { trackSignupFree('integration_cta'); window.location.href = '/signup'; }}
>
  Start FREE In Under 2 Minutes
  <FaArrowRight style={{ marginLeft: '10px' }} />
</button>              {/* <button className="btn-secondary">View Full Documentation</button> */}
            </div>
            <p className="cta-trust">✓ Branded page  ✓ Website-ready proof  ✓ No credit card required</p>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default Integration;
